import { CHALLENGE_SURVEY_OPTIONS, constants, SESSION_SURVEY_OPTIONS, logActivity, logError, LogCategory, LogLevel } from "utils";
import { useOkoSessionContext } from "../useOkoSession/useOkoSession";
import { useSessionContext } from "../useSession";
import { useAuthUserDataContext } from "../useAuthUserData/useAuthUserData";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { request } from "../useOkoApi/useOkoApi";
import { useDataTrack } from "../livekit/useDataTrack/useDataTrack";
import { useActivity } from "../useActivity/useActivity";
import { dataTrackHandlers } from "./dataTrackHandlers";
import { createPhaseDwellTracker, isSoloPhaseState } from "./phaseDwellTracker";
import { shouldSuppressSoloTransition } from "./soloTransition";
import { isRepeatMcAnswer } from "./repeatMcAnswer";
import { initialProgress, advanceProgress, questionOrderOf, rememberTip, tipForQuestion } from "./leftPanelProgress";
import { drawTalkMoves } from "./talkMoves";

const CHALLENGE_SURVEY = "challenge_survey";
const SESSION_SURVEY = "session_survey";

const ANSWARE_OPTION = {
  MULTIPLE: "MULTIPLE_CHOICE",
  OPEN: "OPEN_RESPONSE",
}

export const SCREEN = {
  CHALLENGE: "challenge_screen",
  COLOR: "color_screen",
  CHANGE: "screen_change"
}

const EVENT_STATE = {
  // SOLO_TIME
  CHALLENGE: "challenge",
  GATHER: "gather",
  // TEAM_TIME
  DISCUSS: "discuss",
  SOLUTION: "solution",
  EXPLANATION: "explanation",
  COMPLETE: "complete",
  FEEDBACK_ANALYSIS: "feedback_analysis",
}

const stripTags = (str) => {
  if (str === null || str === undefined || str === "") return "";
  return String(str).replace(/<[^>]*>/g, "");
};

export const useConsensusActivity = () => {

  const {
    consensusSession: { multipleChoiceQuestionResponse, openResponseQuestionResponse },
  } = useOkoSessionContext();
  const { currentTransitionState, setCurrentTransitionState, setCurrentEventState, isShowTransitionView, setIsShowTransitionView, groupCode, userRole, authMethod } = useSessionContext();
  const { authUserData } = useAuthUserDataContext();
  const { activity, session, update: updateActivity } = useActivity();
  const [timerValue, setTimerValue] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [disableOptions, setDisableOptions] = useState(false);
  const [hideQuestion, setHideQuestion] = useState(true);
  const [isChallengeSolved, setIsChallengeSolved] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [isShowRequestMoreTimeButton, setIsShowRequestMoreTimeButton] = useState(false);
  const [isMoreTimeRequested, setIsMoreTimeRequested] = useState(false);
  const [transitionViewText, setTransitionViewText] = useState(constants.SOLO_TIME.toUpperCase());
  const [transitionViewSubtext, setTransitionViewSubtext] = useState("");
  // True while a phase change is being announced to screen readers without a
  // visible overlay (Solo Time re-entry between questions).
  const [announcePhase, setAnnouncePhase] = useState(false);
  const [surveyData, setSurveyData] = useState(null);
  const [prepareToShare, setPrepareToShare] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [keyTerms, setKeyTerms] = useState(null);
  const [keyTermsDefinition, setKeyTermsDefinition] = useState(null);
  const [showStartNewSessionButton, setShowStartNewSessionButton] = useState(null);
  const [isSpokenPromptUsed, setIsSpokenPromptUsed] = useState(false);
  const [mcSubmissionError, setMcSubmissionError] = useState(null);
  const [timeTrack, setTimeTrack] = useState(0);
  const surveyTimeoutRef = useRef(null);
  // The send still in flight, as { index }: one object per send so two sends of
  // the same option are told apart. selectedIndex only changes once the POST
  // resolves, and a ref (not state) is visible to the next tap before React
  // re-renders.
  const pendingMcRef = useRef(null);
  // Also drops the in-flight send, so one still pending from the previous
  // phase neither blocks that option nor selects it when its reply lands.
  const clearSelection = () => {
    setSelectedIndex(null);
    setMcSubmissionError(null);
    pendingMcRef.current = null;
  };
  // Set once the session-complete state lands. A ref, not state, so the
  // delayed challenge-screen hides read the latest value when they fire.
  const isSessionCompleteRef = useRef(false);
  const [discussionAnalysis, setDiscussionAnalysis] = useState(null);
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false);
  const [showDiscussionAnalyzed, setShowDiscussionAnalyzed] = useState(false);
  const hasAnalysisDataRef = useRef(false);
  const [studentResponses, setStudentResponses] = useState(null);
  const [previousRoundResponses, setPreviousRoundResponses] = useState(null);
  const [progress, setProgress] = useState(initialProgress);
  // question_order of the data-track event being handled, so a tip is tagged
  // with the question it was written for rather than the one on screen.
  const eventQuestionRef = useRef(null);
  const loggedTalkMovesRef = useRef(null);
  const [teamworkTips, setTeamworkTips] = useState({});
  // Called synchronously while a data-track event is handled, so the ref
  // still holds that event's question.
  const setCollaborationTip = useCallback((text) => {
    const questionNumber = eventQuestionRef.current;
    if (!questionNumber) {
      logActivity("Teamwork tip dropped: event has no question number", {
        ...phaseLogContextRef.current,
        level: LogLevel.WARNING,
        activityType: "consensus",
      });
      return;
    }
    setTeamworkTips((tips) => rememberTip(tips, questionNumber, text));
  }, []);
  const [currentSpeechPhrase, setCurrentSpeechPhrase] = useState("");
  const [isSpeechPlaying, setIsSpeechPlaying] = useState(false);
  const [hasSubmittedChallengeSurvey, setHasSubmittedChallengeSurvey] = useState(false);
  const [postSoloBannerActive, setPostSoloBannerActive] = useState(false);
  const isFirstDiscussRef = useRef(true);

  // Per-phase dwell tracking (how long in Solo Time vs Team Time). One tracker
  // instance for the activity's lifetime; phaseLogContextRef holds the latest
  // session context so the unmount flush logs with fresh (not stale) values.
  const phaseDwellRef = useRef(null);
  if (phaseDwellRef.current === null) phaseDwellRef.current = createPhaseDwellTracker();
  const phaseLogContextRef = useRef({});

  const logPhaseCompleted = (completed, extra = {}) => {
    if (!completed) return;
    logActivity("Activity phase completed", {
      ...phaseLogContextRef.current,
      activityType: "consensus",
      phase: completed.completedPhase,
      nextPhase: completed.nextPhase,
      durationMs: completed.durationMs,
      ...extra,
    });
  };

  // Keep the latest session context so the unmount flush below logs fresh values.
  useEffect(() => {
    phaseLogContextRef.current = {
      sessionId: session?.id,
      groupCode,
      userId: authUserData?.id,
      role: userRole,
      authMethod,
    };
  });

  // On activity unmount, flush the final phase's dwell time so the last phase
  // (which has no following transition) is also recorded.
  useEffect(() => {
    return () => {
      const completed = phaseDwellRef.current?.end(Date.now());
      if (completed) {
        logActivity("Activity phase completed", {
          ...phaseLogContextRef.current,
          activityType: "consensus",
          phase: completed.completedPhase,
          nextPhase: completed.nextPhase,
          durationMs: completed.durationMs,
          reason: "activity_end",
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanupDiscussionAnalyzed = () => {
    setShowDiscussionAnalyzed(false);
    setDiscussionAnalysis(null);
    setIsAnalysisLoading(false);
    hasAnalysisDataRef.current = false;
  };

  const dispatchMoreTimeRequest = async () => {
    await request(`session/${session.id}/challenge/ask_for_more_time`, {
    }).then(() => setIsMoreTimeRequested(true)
    ).catch((e) => {
      console.log(e);
      logError("More time request failed", e, {
        category: LogCategory.ACTIVITY,
        sessionId: session?.id,
        activityType: "consensus",
        groupCode,
      });
    });
  };

  const dispatchSpeakPromptRequest = async () => {
    if (!isSpokenPromptUsed) {
      await request(`session/${session.id}/challenge/speak_prompt`, {
      }).then(() => setIsSpokenPromptUsed(true)
      ).catch((e) => {
        console.log(e);
        logError("Speak prompt request failed", e, {
          category: LogCategory.ACTIVITY,
          sessionId: session?.id,
          activityType: "consensus",
          groupCode,
        });
      });
    }
  };

  const onSurveyResponse = async (type, value) => {
    if (!type || !value) return;
    if (type === CHALLENGE_SURVEY) {
      await request(`session/${session.id}/challenge/survey`, {
        payload: {
          challenge_id: challenge.id,
          challenge_type: challenge.answer_options ? ANSWARE_OPTION.MULTIPLE : ANSWARE_OPTION.OPEN,
          response: value,
        },
      }).catch((e) => {
        console.log(e);
        logError("Challenge survey submission failed", e, {
          category: LogCategory.ACTIVITY,
          sessionId: session?.id,
          activityType: "consensus",
          challengeId: challenge?.id,
          groupCode,
        });
      });
      setHasSubmittedChallengeSurvey(true);
    }

    if (type === SESSION_SURVEY) {
      await request(`feedback/${session.id}/survey`, {
        payload: {
          math_feel_rating: value,
        },
      }).then(() => {
        setShowStartNewSessionButton(true);
      }).catch((e) => {
        console.log(e);
        logError("Session survey submission failed", e, {
          category: LogCategory.ACTIVITY,
          sessionId: session?.id,
          activityType: "consensus",
          groupCode,
        });
      });
    }
  };

  const dismissChallengeSurvey = () => {
    if (surveyTimeoutRef.current) {
      clearTimeout(surveyTimeoutRef.current);
      surveyTimeoutRef.current = null;
    }
    setSurveyData(null);
    setIsShowTransitionView(false);
    setPrepareToShare(true);
  };

  const handleStateChange = (e, event_id, handleScreen = true) => {
    // Log state transition to Sentry
    if (e?.state) {
      const newPhase = isSoloPhaseState(e?.state) ? constants.SOLO_TIME : constants.TEAM_TIME;

      logActivity("Session state changed", {
        sessionId: session?.id,
        activityType: "consensus",
        previousState: e?.previous_state,
        currentState: e?.state,
        transitionState: newPhase,
        groupCode,
        userId: authUserData?.id,
        role: userRole,
        authMethod,
        challengeId: e?.most_recent_challenge_context?.challenge?.id,
      });

      // Phase-level dwell: when Solo Time / Team Time actually flips, log how
      // long the just-finished phase lasted (durationMs).
      logPhaseCompleted(phaseDwellRef.current.transition(newPhase, Date.now()), {
        challengeId: e?.most_recent_challenge_context?.challenge?.id,
      });

      setCurrentEventState(e.state);
    }

    if (e?.previous_state === EVENT_STATE.FEEDBACK_ANALYSIS) {
      cleanupDiscussionAnalyzed();
    }

    // Re-entering Solo Time for the next question: the backend still sends
    // `color_screen` for `challenge`, but we drop the overlay so the student
    // sees the question instead of a blue screen. Hidden synchronously rather
    // than on the SCREEN.CHALLENGE 750ms timer, otherwise the overlay would
    // repaint blue for that window on its way out.
    const suppressSoloTransition = shouldSuppressSoloTransition({
      state: e?.state,
      previousState: e?.previous_state,
      hasSurvey: Boolean(surveyData),
    });

    if (handleScreen) {
      if (suppressSoloTransition) {
        if (surveyTimeoutRef.current) {
          clearTimeout(surveyTimeoutRef.current);
        }
        setIsShowTransitionView(false);
        setAnnouncePhase(true);
      } else {
        setAnnouncePhase(false);
        if (e?.screen === SCREEN.COLOR) {
          if (surveyTimeoutRef.current) {
            clearTimeout(surveyTimeoutRef.current);
          }
          setIsShowTransitionView(true);
        }
        if (e?.screen === SCREEN.CHALLENGE) {
          setTimeout(() => {
            if (isSessionCompleteRef.current) return;
            setIsShowTransitionView(false);
          }, 750);
        }
      }
    }

    const data = { ...e?.most_recent_challenge_context, event_id: event_id };

    if (data?.challenge) {
      /* We parse the challenge from the data directly because the activity.challenge 
      fields are merged, which causes different MC and Open response challenge fields
      from consecutive questions to be merged and displayed into one challenge, which 
      is problematic for rendering. */
      setChallenge(data?.challenge);
    }

    updateActivity(data, "multiple-choice");

    const currentState = e?.state;

    const commonStateUpdate = ({
      challengeSolved = false,
      currentTransitionState = constants.TEAM_TIME,
      transitionViewText = "",
      transitionViewSubtext = "",
    } = {}) => {
      setHideQuestion(false)
      setTimerValue(null);
      setTimeTrack(0);
      setIsShowRequestMoreTimeButton(false);
      setIsChallengeSolved(challengeSolved)
      setCurrentTransitionState(currentTransitionState)
      setTransitionViewText(transitionViewText || currentTransitionState.toUpperCase())
      setTransitionViewSubtext(transitionViewSubtext)
    }

    if (currentState === EVENT_STATE.CHALLENGE) {
      setHideQuestion(true); // Unmounting is required for correct MathJax rendering between challenges.
      commonStateUpdate({
        currentTransitionState: constants.SOLO_TIME,
      })

      setDisableOptions(true);
      clearSelection();
      setExplanation(null);
      setKeyTerms(null);
      setKeyTermsDefinition(null);
      setIsSpokenPromptUsed(false);
      setStudentResponses(null);
      setPreviousRoundResponses(null);
      setPrepareToShare(false);
      setHasSubmittedChallengeSurvey(false);
      setPostSoloBannerActive(false);
      isFirstDiscussRef.current = true;
      cleanupDiscussionAnalyzed();
    }

    if (currentState === EVENT_STATE.GATHER) {
      commonStateUpdate({
        currentTransitionState: constants.SOLO_TIME,
      })

      setTimeout(() => {
        setDisableOptions(false);
      }, 10000);
      setExplanation(null);
      setKeyTerms(null);
      setKeyTermsDefinition(null);
      setPreviousRoundResponses(null);
      cleanupDiscussionAnalyzed();
    }

    if (currentState === EVENT_STATE.DISCUSS) {
      const { previous_context: discussPreviousContext } = e ?? {};

      // Capture previous round responses for purple dot indicators
      if (discussPreviousContext?.responses && Object.keys(discussPreviousContext.responses).length > 0) {
        setPreviousRoundResponses(discussPreviousContext.responses);
      } else {
        setPreviousRoundResponses(null);
      }

      commonStateUpdate()

      clearSelection();
      setDisableOptions(false);
      setSurveyData(null);
      setPrepareToShare(false);

      if (isFirstDiscussRef.current) {
        isFirstDiscussRef.current = false;
        if (!hasSubmittedChallengeSurvey) {
          setPostSoloBannerActive(true);
        }
      } else {
        setPostSoloBannerActive(false);
      }
    }

    if (currentState === EVENT_STATE.SOLUTION) {
      const { context, previous_context } = e ?? {}

      // Extract student responses from previous_context
      if (previous_context?.responses) {
        setStudentResponses(previous_context.responses);
      }
      setPreviousRoundResponses(null);
      const isSoloTime = currentTransitionState === constants.SOLO_TIME;
      const getSolutionText = () => {
        if (context?.challenge_solved) {
          return isSoloTime ? constants.GREAT_WORK : constants.GREAT_TEAM_WORK;
        }
        return isSoloTime ? constants.SOLO_TIME.toUpperCase() : constants.TEAM_TIME.toUpperCase();
      };

      commonStateUpdate({
        challengeSolved: context?.challenge_solved,
        currentTransitionState: isSoloTime ? constants.SOLO_TIME : constants.TEAM_TIME,
        transitionViewText: getSolutionText()
      })

      clearSelection();
      setDisableOptions(true);
      setSurveyData(null);
      setExplanation(null);
      setKeyTerms(null);
      setKeyTermsDefinition(null);
      setPostSoloBannerActive(false);
    }

    if (currentState === EVENT_STATE.FEEDBACK_ANALYSIS) {
      const context = e?.context;
      const analysisText = context?.analysis_text;

      if (analysisText === "Analysis pending..." && !hasAnalysisDataRef.current) {
        setIsAnalysisLoading(true);
        setShowDiscussionAnalyzed(true);
        setDiscussionAnalysis(null);
      }

      if (analysisText && analysisText !== "Analysis pending...") {

        try {
          const extractLevelNumber = (levelString) => {
            if (!levelString) return 3;
            if (typeof levelString === 'number') return levelString;
            const match = levelString.match(/^(\d+)/);
            return match ? parseInt(match[1], 10) : 3;
          };

          const getBadgeTitle = (levelString) => {
            if (!levelString) return "Big Leagues";
            if (typeof levelString === 'number') return "Big Leagues";

            const parts = levelString.split(' - ');
            if (parts.length > 1) {
              return parts[1].trim();
            }

            return "Big Leagues";
          };

          let studentFacingReport;
          if (typeof analysisText === 'string' && analysisText.includes('```')) {
            const jsonMatch = analysisText.match(/```json\n([\s\S]*?)\n```/) ||
              analysisText.match(/```\n([\s\S]*?)\n```/);
            const jsonString = jsonMatch ? jsonMatch[1] : analysisText;
            const analysisData = JSON.parse(jsonString);
            studentFacingReport = analysisData.studentFacingReport;
          } else {
            studentFacingReport = {
              collaborationLevel: context.collaborationLevel,
              topQuote: context.topQuote || null,
              topQuotes: context.topQuotes || [],
              studentFacingFeedback: context.studentFacingFeedback,
            };
          }

          const collaborationLevelString = studentFacingReport.collaborationLevel;
          const level = extractLevelNumber(collaborationLevelString);
          const topQuotes = studentFacingReport.topQuotes || [];
          const studentFacingFeedback = studentFacingReport.studentFacingFeedback;
          const collaborationTip = studentFacingReport.collaborationTip;
          const badgeTitle = getBadgeTitle(collaborationLevelString);
          const badgeType = "Collaboration";

          const firstQuote = studentFacingReport.topQuote || (topQuotes.length > 0 ? topQuotes[0] : null);

          setDiscussionAnalysis({
            level: level,
            quote: firstQuote?.quote || "",
            name: firstQuote?.student || "",
            feedback: studentFacingFeedback,
            collaborationTip: collaborationTip,
            badgeTitle: badgeTitle,
            badgeType: badgeType,
          });

          // Persist collaborationTip separately so it survives cleanup
          if (collaborationTip) {
            setCollaborationTip(collaborationTip);
          }

          setIsAnalysisLoading(false);
          setShowDiscussionAnalyzed(true);
          hasAnalysisDataRef.current = true;
        } catch (error) {
          console.error('Error parsing analysis data in state_change:', error);
          // Only show loading if we haven't received badge data yet
          if (!hasAnalysisDataRef.current) {
            setIsAnalysisLoading(true);
            setShowDiscussionAnalyzed(true);
          }
        }
      }
    }

    if (currentState === EVENT_STATE.EXPLANATION) {
      const explanation_text = e?.context?.explanation_text;
      const final_round = e?.context?.final_round;

      commonStateUpdate({
        transitionViewSubtext: final_round ? constants.FINAL_ROUND : "",
      })

      clearSelection();
      setDisableOptions(true);
      setPreviousRoundResponses(null);
      setPostSoloBannerActive(false);

      if (Array.isArray(explanation_text) && explanation_text.length > 0) {
        setExplanation(explanation_text.slice(0, -1));
        setKeyTerms(explanation_text[explanation_text.length - 1]);
        setKeyTermsDefinition(explanation_text[1]);
      } else {
        logActivity("EXPLANATION event missing or invalid explanation_text", {
          level: LogLevel.WARNING,
          category: LogCategory.ACTIVITY,
          sessionId: session?.id,
          groupCode,
          rawEvent: JSON.stringify(e),
        });
      }
    }

    if (currentState === EVENT_STATE.COMPLETE) {
      isSessionCompleteRef.current = true;
      setPreviousRoundResponses(null);
      if (e.previous_state === "solution") {
        setIsChallengeSolved(false);
      }
      setSurveyData({
        type: "session_survey",
        question: "Great effort today!  Let your teacher know how you felt about the math you did today!",
        options: SESSION_SURVEY_OPTIONS
      });
      if (surveyTimeoutRef.current) {
        clearTimeout(surveyTimeoutRef.current);
      }
      setIsShowTransitionView(true);
      setPostSoloBannerActive(false);
      cleanupDiscussionAnalyzed();
    }
  };

  useDataTrack((e) => {
    eventQuestionRef.current = questionOrderOf(e);
    setProgress((prev) => advanceProgress(prev, e));
    if (e?.event_type === "speech_start") {
      setCurrentSpeechPhrase(stripTags(e?.data?.message));
      setIsSpeechPlaying(true);
    }
    if (e?.event_type === "speech_end") {
      // Stop the mouth animation but keep the text visible — the bubble persists
      // until a new phrase replaces it or the student closes it (no auto-hide).
      setIsSpeechPlaying(false);
    }
    dataTrackHandlers({
      dataTrackEvent: e,
      timeTrack,
      setTimerValue,
      setTimeTrack,
      setIsShowRequestMoreTimeButton,
      setIsMoreTimeRequested,
      surveyTimeoutRef,
      isSessionCompleteRef,
      setIsShowTransitionView,
      clearTimeout,
      handleStateChange,
      setDiscussionAnalysis,
      setIsAnalysisLoading,
      setShowDiscussionAnalyzed,
      hasAnalysisDataRef,
      setCollaborationTip,
      hasSurvey: Boolean(surveyData),
      setAnnouncePhase,
      sessionContext: {
        sessionId: session?.id,
        groupCode,
        userId: authUserData?.id,
        role: userRole,
        authMethod,
      },
    })
  });

  const onORAnswer = async (solution) => {
    const isResubmit = selectedIndex !== null;

    // Log open response answer submission
    logActivity("Answer submitted", {
      sessionId: session?.id,
      activityType: "consensus",
      questionType: "open_response",
      answerText: solution,
      isResubmit,
      challengeId: challenge?.id,
      transitionState: currentTransitionState,
      groupCode,
      userId: authUserData?.id,
      role: userRole,
      authMethod,
    });

    try {
      await openResponseQuestionResponse(activity, solution);
      setSelectedIndex("flagging OR answer submission");
      setIsShowRequestMoreTimeButton(false);
      if (currentTransitionState === constants.SOLO_TIME && !isResubmit) {
        setSurveyData({
          type: "challenge_survey",
          question: "How did you feel about solving this problem?",
          options: CHALLENGE_SURVEY_OPTIONS
        });
        setIsShowTransitionView(true);
      }
    } catch (error) {
      console.error("Error submitting OR answer:", error);
      logError("Open response answer submission failed", error, {
        category: LogCategory.ACTIVITY,
        sessionId: session?.id,
        activityType: "consensus",
        challengeId: challenge?.id,
        groupCode,
      });
      throw error; // Re-throw so OpenResponseWidget.handleClick catch sets isSubmissionError
    }
  }

  const onMCAnswer = async (option, index) => {
    if (disableOptions) return;
    if (!option.eligible) return;
    if (isRepeatMcAnswer(index, selectedIndex, pendingMcRef.current?.index)) {
      // The tapped answer is saved or on its way, so an earlier failure no
      // longer applies.
      setMcSubmissionError(null);
      return;
    }

    const isResubmit = selectedIndex !== null;

    // Log multiple choice answer submission
    logActivity("Answer submitted", {
      sessionId: session?.id,
      activityType: "consensus",
      questionType: "multiple_choice",
      selectedIndex: index,
      selectedOption: option?.text || option?.label,
      isResubmit,
      challengeId: challenge?.id,
      transitionState: currentTransitionState,
      groupCode,
      userId: authUserData?.id,
      role: userRole,
      authMethod,
    });

    const send = { index };
    pendingMcRef.current = send;
    try {
      await multipleChoiceQuestionResponse(activity, index);
      // A reply that comes back after a newer pick, or after the round moved
      // on, must not replace what the student sees now.
      if (pendingMcRef.current !== send) return;
      setSelectedIndex(index);
      setIsShowRequestMoreTimeButton(false);
      setMcSubmissionError(null);

      if (currentTransitionState === constants.SOLO_TIME && !isResubmit) {
        setSurveyData({
          type: "challenge_survey",
          question: "How did you feel about solving this problem?",
          options: CHALLENGE_SURVEY_OPTIONS
        });
        setIsShowTransitionView(true);
      }
    } catch (error) {
      console.error("Error submitting MC answer:", error);
      logError("Multiple choice answer submission failed", error, {
        category: LogCategory.ACTIVITY,
        sessionId: session?.id,
        activityType: "consensus",
        challengeId: challenge?.id,
        groupCode,
      });
      // Stale, as above.
      if (pendingMcRef.current !== send) return;
      setMcSubmissionError(error?.detail || error?.message || "Failed to submit answer. Please try again.");
    } finally {
      // Only clear our own send; a newer pick may own the ref by now.
      if (pendingMcRef.current === send) pendingMcRef.current = null;
    }
  };

  useEffect(() => {
    if (typeof window?.MathJax !== "undefined") {
      window.MathJax.typeset()
    }
  }, [activity]);

  useEffect(() => {
    if (isChallengeSolved) {
      setIsMoreTimeRequested(false);
    }
  }, [isChallengeSolved]);

  useEffect(() => {
    if (surveyData?.type === "session_survey" && surveyTimeoutRef.current) {
      clearTimeout(surveyTimeoutRef.current);
      surveyTimeoutRef.current = null;
    }
  }, [surveyData]);

  useEffect(() => {
    return () => {
      if (surveyTimeoutRef.current) {
        clearTimeout(surveyTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (timeTrack <= 0) return;

    const intervalId = setInterval(() => {
      setTimeTrack(prev => {
        if (prev === null || prev <= 0) return prev;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timeTrack]);

  const dismissPostSoloBanner = () => setPostSoloBannerActive(false);

  const talkMoves = useMemo(
    () => (session?.id && progress.questionNumber ? drawTalkMoves(session.id, progress.questionNumber) : null),
    [session?.id, progress.questionNumber]
  );

  useEffect(() => {
    if (currentTransitionState !== constants.TEAM_TIME) return;
    const logKey = `${progress.questionNumber}:${talkMoves ? "shown" : "none"}`;
    if (loggedTalkMovesRef.current === logKey) return;
    loggedTalkMovesRef.current = logKey;
    if (!talkMoves) {
      logActivity("Talk moves missing in Team Time", {
        ...phaseLogContextRef.current,
        level: LogLevel.WARNING,
        activityType: "consensus",
        questionNumber: progress.questionNumber,
        hasSessionId: Boolean(session?.id),
      });
      return;
    }
    logActivity("Talk moves shown", {
      ...phaseLogContextRef.current,
      activityType: "consensus",
      questionNumber: progress.questionNumber,
      talkMoves: talkMoves.map(({ label, text }) => `${label}: ${text}`),
    });
  }, [currentTransitionState, talkMoves, progress.questionNumber, session?.id]);

  return {
    timerValue,
    hideQuestion,
    disableOptions,
    selectedIndex,
    isShowRequestMoreTimeButton,
    isMoreTimeRequested,
    isChallengeSolved,
    currentTransitionState,
    transitionViewText,
    transitionViewSubtext,
    surveyData,
    isShowTransitionView,
    announcePhase,
    explanation,
    keyTerms,
    keyTermsDefinition,
    challenge,
    onORAnswer,
    onMCAnswer,
    mcSubmissionError,
    dispatchMoreTimeRequest,
    dispatchSpeakPromptRequest,
    onSurveyResponse,
    dismissChallengeSurvey,
    showStartNewSessionButton,
    isSpokenPromptUsed,
    setMcSubmissionError,
    discussionAnalysis,
    isAnalysisLoading,
    showDiscussionAnalyzed,
    session,
    studentResponses,
    previousRoundResponses,
    collaborationTip: tipForQuestion(teamworkTips, progress.questionNumber),
    questionNumber: progress.questionNumber,
    teamRound: progress.teamRound,
    talkMoves,
    prepareToShare,
    currentSpeechPhrase,
    isSpeechPlaying,
    postSoloBannerActive,
    dismissPostSoloBanner,
  }
};
